/**
 * Guard against the React ↔ machine-translation crash.
 *
 * Chrome/Edge "translate this page" swaps the text nodes React created for its
 * own. React keeps pointing at the originals, so the next unmount calls
 * `removeChild` (or `insertBefore`) on a node that no longer belongs to that
 * parent → `NotFoundError` → the whole subtree dies and an ErrorBoundary card
 * takes its place.
 *
 * Reproduced 2026-08-01: translate the market page, then switch symbol — the
 * whole ticker subtree unmounts at once and every translated text node under it
 * is stale. Switching *tabs* mostly mounts fresh nodes, which is why that path
 * looked fine.
 *
 * This makes the two DOM calls no-op instead of throwing when the parent no
 * longer matches. Translation still works — we only skip a mutation that was
 * already impossible. Worst case is a stale translated string lingering for one
 * render; the alternative is a dead section.
 *
 * Must run before the first React render (see main.tsx).
 */

/** How many suppressions to log before going quiet (avoid console spam). */
const WARN_LIMIT = 5;

let installed = false;
let hits = 0;

/** Number of suppressed mutations this session — attach to error reports. */
export function translateGuardHits(): number {
  return hits;
}

/** True when the browser has visibly translated the page (Chrome/Edge marker). */
export function pageIsMachineTranslated(): boolean {
  if (typeof document === "undefined") return false;
  const cls = document.documentElement.className;
  return cls.includes("translated-ltr") || cls.includes("translated-rtl");
}

function note(op: string, node: Node): void {
  hits += 1;
  if (hits > WARN_LIMIT) return;
  console.warn(
    `[translate-guard] suppressed ${op} on a detached node ` +
      `(machineTranslated=${pageIsMachineTranslated()}, hits=${hits})`,
    node,
  );
}

export function installTranslateCrashGuard(): void {
  if (installed || typeof Node === "undefined") return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildGuarded<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      note("removeChild", child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeGuarded<T extends Node>(
    this: Node,
    node: T,
    reference: Node | null,
  ): T {
    if (reference && reference.parentNode !== this) {
      note("insertBefore", node);
      return node;
    }
    return originalInsertBefore.call(this, node, reference) as T;
  };
}
